/**
 * Service for handling JWT token refresh
 * This service provides functions for refreshing tokens before they expire
 */

import { generateToken, verifyToken, saveToken, getToken } from './tokenService';
import Parse from '../../../config/parse';

// Threshold in milliseconds before token expiry to trigger refresh (1 day)
const REFRESH_THRESHOLD = 24 * 60 * 60 * 1000;

/**
 * Check if token needs to be refreshed
 * @returns {Boolean} True if token needs refresh, false otherwise
 */
export const needsRefresh = () => {
  const token = getToken();
  if (!token) return false;
  
  const decoded = verifyToken(token);
  if (!decoded) return false;
  
  // Check if token will expire within the threshold
  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  
  return (expiryTime - currentTime) < REFRESH_THRESHOLD;
};

/**
 * Refresh the JWT token if the current user is authenticated
 * @returns {Promise<String|null>} New token or null if refresh failed
 */
export const refreshToken = async () => {
  try {
    // Get current user from Parse
    const currentUser = await Parse.User.current();
    if (!currentUser) return null;
    
    // Generate new token
    const newToken = generateToken(currentUser);
    
    // Save new token
    saveToken(newToken);
    
    console.log('Token refreshed successfully');
    return newToken;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

/**
 * Setup automatic token refresh
 * This function sets up an interval to check and refresh the token
 */
export const setupAutoRefresh = () => {
  // Check for token refresh every hour
  const CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour
  
  // Set up interval to check for token refresh
  const intervalId = setInterval(async () => {
    if (needsRefresh()) {
      try {
        await refreshToken();
      } catch (error) {
        console.error('Error during automatic token refresh:', error);
      }
    }
  }, CHECK_INTERVAL);
  
  // Return function to clear interval
  return () => clearInterval(intervalId);
};
